import { BrowserAuthError } from '@azure/msal-browser'
import { msalInstance, loginRequest } from './msal'
import { clearImpersonationSession, getImpersonationSession } from './impersonation'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

// Serializes every interactive-redirect attempt through one in-flight promise.
// Without this, several queries firing on mount (Dashboard.tsx's stats/recent/
// integrations, useCurrentUser's /auth/me) each independently call
// acquireTokenSilent against the same stale token, all fail around the same
// moment, and each tries acquireTokenRedirect near-simultaneously. MSAL
// refuses to start a second interactive flow while one is already in
// progress and throws BrowserAuthError('interaction_in_progress') for every
// caller after the first -- and since navigating away is not instantaneous,
// those callers' rejections reach React Query and render as a generic
// failure before the browser ever leaves the page. Routing every caller
// through this one promise means only the first actually calls MSAL; the
// rest just await the same in-flight attempt instead of racing it.
let redirectInFlight: Promise<never> | null = null

// The "interaction already in progress" flag MSAL keeps in sessionStorage
// does not clear on a page refresh -- only on tab close (see msal.ts's cache
// config comment: refresh tokens live in localStorage specifically so a new
// tab stays authenticated; sessionStorage is what MSAL itself uses for this
// short-lived flag, independent of that choice). A previous version of this
// function only cleared it when interaction_in_progress came directly from
// acquireTokenSilent -- not when it came from a nested acquireTokenRedirect
// call reacting to some *other* error, which is exactly what happens above
// once one caller has already set the flag. Left uncleared, every future
// page load (not just this session) hits the same stuck flag and fails the
// same way -- matching the reported symptom that a refresh doesn't recover,
// but manually clearing storage does. Centralizing the redirect through one
// function, always with this same recovery path, closes that gap regardless
// of which caller or which upstream error triggered it.
async function redirectToLogin(): Promise<never> {
  if (redirectInFlight) return redirectInFlight

  redirectInFlight = (async (): Promise<never> => {
    try {
      await msalInstance.acquireTokenRedirect(loginRequest)
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
        Object.keys(sessionStorage)
          .filter(k => k.includes('msal') && k.includes('interaction'))
          .forEach(k => sessionStorage.removeItem(k))
        await msalInstance.acquireTokenRedirect(loginRequest)
      } else {
        throw err
      }
    }
    // acquireTokenRedirect navigates away on success -- this line is only
    // reached if navigation was somehow suppressed, which should not happen
    // in a real browser. Throwing (rather than returning) keeps this
    // function's return type honest and ensures callers never treat a
    // redirect attempt as if it produced a token.
    throw new Error('Redirected to login')
  })()

  try {
    return await redirectInFlight
  } finally {
    redirectInFlight = null
  }
}

async function getAccessToken(): Promise<string> {
  // Prefer the account MSAL itself considers active (set explicitly after
  // login -- see main.tsx) over guessing from getAllAccounts(). With the
  // authority at /organizations, a browser can legitimately hold cached
  // accounts from two different Entra tenants; accounts[0] has no defined
  // ordering and no relationship to which one the user is actually trying to
  // use right now.
  let account = msalInstance.getActiveAccount()

  if (!account) {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 1) {
      account = accounts[0]!
      msalInstance.setActiveAccount(account)
    } else if (accounts.length > 1) {
      // Genuinely ambiguous -- silently picking one risks authenticating
      // against the wrong tenant's data path, which this app treats as a
      // real isolation concern, not just a UX rough edge. There is no
      // account-picker UI today, so the only safe move is to force a fresh
      // interactive sign-in rather than guess.
      return redirectToLogin()
    } else {
      throw new Error('Not authenticated')
    }
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account })
    return result.accessToken
  } catch {
    // Any acquireTokenSilent failure -- not just InteractionRequiredAuthError
    // -- means this cached account cannot currently produce a usable token.
    // A narrower check here is exactly how this bug shipped: a stale or
    // orphaned cache entry that fails in some other way silently fell
    // through as an unhandled rejection instead of prompting re-auth.
    return redirectToLogin()
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    // Set only on NO_INVITE_FOUND (withPooledUserAuth.ts) -- the email
    // address the caller's token actually presented (preferred_username),
    // so the UI can show it back to the user without re-parsing `message`.
    public readonly presentedEmail?: string | null,
    // Set only on TENANT_NOT_PROVISIONED (withPooledUserAuth.ts) -- the slug
    // POST /admin/tenant/migrate needs, and whether this caller matches the
    // tenant's pre-registered admin secret. See lib/authErrorMessages.ts /
    // stores/auth.ts's provisioningInfo, the only consumer of both.
    public readonly tenantSlug?: string | null,
    public readonly canProvision?: boolean
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  // Checked before MSAL, not after: an impersonation tab has no real MSAL
  // account for getAccessToken() to find (see lib/impersonation.ts), so
  // falling through to it on every request would just fail every call.
  // getImpersonationSession() already treats an expired session as absent,
  // so this naturally stops taking effect the moment the 30-minute window
  // closes, without this file needing its own expiry check.
  const impersonation = getImpersonationSession()
  const token = impersonation ? impersonation.token : await getAccessToken()

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as {
      error?: {
        message?: string
        code?: string
        presentedEmail?: string | null
        tenantSlug?: string | null
        canProvision?: boolean
      }
    }
    if (response.status === 401) {
      if (impersonation) {
        // Never route an impersonation tab through redirectToLogin(): it
        // unconditionally calls MSAL's acquireTokenRedirect, and this tab
        // has no real MSAL account (nor should the admin necessarily have
        // one for this specific tenant) -- that would send the admin into
        // this tenant's own Entra login rather than ending the session.
        // The server only ever 401s an impersonation token for a reason
        // this tab can't recover from client-side (expired, or the tenant
        // was suspended mid-session -- see withAuthPooled.ts's
        // impersonation branch), so the only correct move is to end it.
        clearImpersonationSession()
        window.location.replace('/')
        throw new ApiError('Impersonation session ended', 401, 'IMPERSONATION_SESSION_ENDED')
      }
      // A server-side 401 despite getAccessToken() having returned a token
      // means the server rejected it (expired between acquisition and use,
      // or revoked) -- same recovery as an acquireTokenSilent failure, and
      // routed through the same deduped, stuck-flag-aware path rather than
      // calling acquireTokenRedirect directly here too.
      await redirectToLogin()
      // redirectToLogin() only returns by throwing -- execution does not reach here
    }
    throw new ApiError(
      body.error?.message ?? response.statusText,
      response.status,
      body.error?.code,
      body.error?.presentedEmail,
      body.error?.tenantSlug,
      body.error?.canProvision
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { method: 'GET', ...options }),

  post: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    }),

  put: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    }),

  patch: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
      ...options,
    }),

  delete: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { method: 'DELETE', ...options }),

  /** SSE streaming — returns an EventSource for AI chat */
  stream: (path: string, body: unknown): EventSource => {
    const url = new URL(`${API_BASE}${path}`, window.location.origin)
    // Pass body as base64 query param — backend reads it to bootstrap the stream
    url.searchParams.set('payload', btoa(JSON.stringify(body)))
    return new EventSource(url.toString())
  },
}
